import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { FiDollarSign, FiGlobe, FiMail, FiShoppingBag, FiUser } from "react-icons/fi";
import { useToast } from "../../components/Toast/ToastContext";
import { register } from "../../store/auth/actions";
import { hasPendingPlan } from "../../services/webBilling";
import { AppDispatch } from "../../store/store";
import { COUNTRY_CURRENCY } from "../../types/countryCurrency";
import {
  AuthButton,
  AuthError,
  AuthField,
  AuthHeader,
  AuthLink,
  AuthSelect,
  AuthSwitch,
  PasswordField,
} from "./authUi";

// Same minimum the server applies when a password is changed or reset.
const MIN_PASSWORD_LENGTH = 6;

type Fields = "name" | "email" | "password" | "confirmPassword" | "terms";

const COUNTRY_OPTIONS = COUNTRY_CURRENCY.map((c) => ({
  value: c.countryCode,
  label: c.countryName,
}));

// One entry per currency, as several countries share one.
const CURRENCY_OPTIONS = Array.from(
  new Map(
    COUNTRY_CURRENCY.map((c) => [
      c.currencyCode,
      // Code first, so it stays visible if a narrow box cuts the name off.
      { value: c.currencyCode, label: `${c.currencyCode} · ${c.currencyName}` },
    ])
  ).values()
);

const currencyFor = (countryCode: string) =>
  COUNTRY_CURRENCY.find((c) => c.countryCode === countryCode)?.currencyCode ?? "";

/** Sign-ups start on United States / USD; people elsewhere pick theirs. */
const DEFAULT_COUNTRY = "US";

interface SignupProps {
  onNeedsVerification?: (email: string, password: string) => void;
  onSwitchToLogin: () => void;
}

const Signup: React.FC<SignupProps> = ({ onNeedsVerification, onSwitchToLogin }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [currency, setCurrency] = useState(currencyFor(DEFAULT_COUNTRY));
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Fields, string>>>({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const clearError = (field: Fields) => {
    setFieldErrors((f) => ({ ...f, [field]: undefined }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: Partial<Record<Fields, string>> = {};
    if (!name.trim()) errors.name = "Enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) errors.email = "Enter a valid email.";
    if (password.length < MIN_PASSWORD_LENGTH)
      errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    else if (password !== confirmPassword) errors.confirmPassword = "Passwords don't match.";
    if (!agreeTerms) errors.terms = "Please agree to the Terms and Privacy Policy.";
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      await dispatch(
        register(name.trim(), email.trim(), password, { businessName, country, currency })
      );
      notify("Account created. Check your email for a code.", { type: "success" });
      onNeedsVerification?.(email.trim(), password);
    } catch (err: any) {
      setError(err?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthHeader
        title="Create your free account"
        subtitle={
          hasPendingPlan()
            ? "Verify your email and we'll take you straight back to checkout."
            : "Free forever for up to 10 events and 25 items. No credit card."
        }
      />
      <form onSubmit={handleSubmit} noValidate>
        <AuthError message={error} />
        <AuthField
          label="Your name"
          name="name"
          autoComplete="name"
          autoFocus
          placeholder="Sam Rivera"
          icon={<FiUser />}
          value={name}
          error={fieldErrors.name}
          onChange={(e) => {
            setName(e.target.value);
            clearError("name");
          }}
        />
        <AuthField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@example.com"
          icon={<FiMail />}
          value={email}
          error={fieldErrors.email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearError("email");
          }}
        />
        <PasswordField
          label="Password"
          name="password"
          autoComplete="new-password"
          placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
          value={password}
          error={fieldErrors.password}
          onChange={(e) => {
            setPassword(e.target.value);
            clearError("password");
          }}
        />
        <PasswordField
          label="Re-type password"
          name="confirmPassword"
          autoComplete="new-password"
          value={confirmPassword}
          error={fieldErrors.confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            clearError("confirmPassword");
          }}
        />
        <AuthField
          label="Business name (optional)"
          name="businessName"
          autoComplete="organization"
          placeholder="Birchwood Market Co."
          icon={<FiShoppingBag />}
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
        />
        <div className="grid gap-x-3 sm:grid-cols-2">
          <AuthSelect
            label="Country"
            name="country"
            placeholder="Select country"
            icon={<FiGlobe />}
            options={COUNTRY_OPTIONS}
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
              setCurrency(currencyFor(e.target.value));
            }}
          />
          <AuthSelect
            label="Currency"
            name="currency"
            placeholder="Select currency"
            icon={<FiDollarSign />}
            options={CURRENCY_OPTIONS}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          />
        </div>

        <label className="mb-1 mt-1 flex cursor-pointer items-start gap-3 text-sm leading-6 text-slate-400">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={() => {
              setAgreeTerms((v) => !v);
              clearError("terms");
            }}
            className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-blue-500"
          />
          <span>
            I agree to the{" "}
            <a
              href="/terms-and-condition"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-300 hover:underline"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-300 hover:underline"
            >
              Privacy Policy
            </a>
          </span>
        </label>
        {fieldErrors.terms && <p className="mb-2 text-xs text-red-300">{fieldErrors.terms}</p>}

        <AuthButton loading={loading}>Create account</AuthButton>
      </form>
      <AuthSwitch>
        Already have an account? <AuthLink onClick={onSwitchToLogin}>Log in</AuthLink>
      </AuthSwitch>
    </>
  );
};

export default Signup;
